/**
 * JsonStringifier - Utility for safe JSON stringification
 * 
 * Extracted from TypesetterWorkerClient for better modularity and testability.
 * 
 * This utility provides functions for safely stringifying objects to JSON,
 * with fallback handling for null/undefined values and error cases.
 */

/**
 * Safely stringify an object to JSON with fallback
 * 
 * Attempts to stringify the value. Returns 'null' if value is null/undefined,
 * or a fallback string if stringification fails.
 * 
 * @param value - Value to stringify
 * @param fallback - Fallback string if stringification fails (default: 'null')
 * @returns JSON string, or fallback if value is null/undefined or stringification fails
 */
export function safeStringify(value: any, fallback: string = 'null'): string {
  if (value === null || value === undefined) {
    return fallback;
  }
  
  try {
    return JSON.stringify(value);
  } catch (error) {
    console.warn('[JsonStringifier] Failed to stringify value:', error);
    return fallback;
  }
}

/**
 * Stringify telemetry data for logging
 * 
 * Safely stringifies telemetry data, returning 'null' if the data is null/undefined.
 * 
 * @param telemetry - Telemetry data to stringify
 * @returns JSON string or 'null'
 */
export function stringifyTelemetry(telemetry: any): string {
  return safeStringify(telemetry, 'null');
}

/**
 * Stringify with truncation for logging
 * 
 * Safely stringifies a value and truncates it to a maximum length for logging.
 * 
 * @param value - Value to stringify
 * @param maxLength - Maximum length of the string (default: 80)
 * @param fallback - Fallback string if stringification fails (default: 'null')
 * @returns Truncated JSON string with ellipsis if needed
 */
export function stringifyWithTruncation(
  value: any,
  maxLength: number = 80,
  fallback: string = 'null'
): string {
  const str = safeStringify(value, fallback);
  
  if (str.length <= maxLength) {
    return str;
  }
  
  return str.substring(0, maxLength) + '...';
}

